import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, Calendar, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    date_of_birth: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast.success(`Welcome back, ${user.first_name}!`);
        if (user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/portal');
        }
      } else {
        if (!formData.first_name || !formData.last_name || !formData.phone) {
          toast.error("Please fill in all required fields");
          setIsLoading(false);
          return;
        }
        const user = await register(formData);
        toast.success(`Welcome to HealthCentrix, ${user.first_name}!`);
        navigate('/portal');
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="pt-20 min-h-screen flex items-center justify-center miami-grid">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#ff00ff]/10 rounded-full blur-[150px]" />
      
      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-gray-400">
            {isLogin 
              ? "Sign in to access your patient portal" 
              : "Join HealthCentrix for personalized care"}
          </p>
        </div>

        <div className="glass rounded-3xl p-8">
          {/* Toggle */}
          <div className="flex bg-[#1a0b2e] rounded-full p-1 mb-8">
            <button
              data-testid="toggle-login"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${
                isLogin ? 'bg-[#00f0ff] text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              data-testid="toggle-register"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${
                !isLogin ? 'bg-[#00f0ff] text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      name="first_name"
                      data-testid="input-first-name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="John"
                      className="pl-10 bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Last Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      name="last_name"
                      data-testid="input-last-name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      className="pl-10 bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-gray-300 mb-2 block">Email Address *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  name="email"
                  type="email"
                  data-testid="input-email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="pl-10 bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="input-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <>
                <div>
                  <Label className="text-gray-300 mb-2 block">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      name="phone"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="pl-10 bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Date of Birth</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      name="date_of_birth"
                      type="date"
                      data-testid="input-dob"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                      className="pl-10 bg-[#2a1b3d] border-[#4b0082] text-white"
                    />
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              data-testid="submit-auth"
              disabled={isLoading}
              className="w-full bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all disabled:opacity-50"
            >
              {isLoading ? "Please wait..." : (isLogin ? "Sign In" : "Create Account")}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          {isLogin && (
            <p className="text-center text-gray-400 text-sm mt-6">
              Forgot your password? Contact support.
            </p>
          )}
        </div>

        <p className="text-center text-gray-400 text-sm mt-6">
          <Link to="/" className="text-[#00f0ff] hover:underline">
            Back to Home
          </Link>
        </p>
      </div>
    </div>
  );
}
